import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormValidationMode, Icon } from '@ampol/design-system';
import './header-account-selector.scss';
import { setAssignedGrandparentAccount } from '../../actions/userActions';
import { getGrandparentAccountNameRequest, getGrandparentAccountNameReset } from '../../actions/accountActions';
import OutsideClickHandler from 'react-outside-click-handler/esm/OutsideClickHandler';
import * as yup from 'yup';
import { string } from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCashOrdersListReset } from 'actions/cashActions';
import PageLoader from '../page-loader/page-loader';
import queryString from 'query-string';
import Env from 'Env';
import { getPortalType } from 'utilities/core-utilities';
import Validation from 'constants/validation-messages';
import { DropdownSearch } from '@ampol/design-system/lib';
import { trackCustomEvent } from 'utilities/app-insights';
import { UserState } from 'types/UserState';

interface HeaderAccountProps {
  allowCashAccountsOnly: boolean;
}

const HeaderAccountSelector = ({ allowCashAccountsOnly }: HeaderAccountProps) => {
  const dispatch = useDispatch();
  const validAccountRegex = allowCashAccountsOnly ? /^05[0-9]{8}$/ : /^[0-9]{10}$/;
  const navigate = useNavigate();
  const location = useLocation();
  const isMounted = useRef(false);

  const [showSearch, setShowSearch] = useState(true);
  const [searchValueChanged, setSearchValueChanged] = useState(false);
  const isInitialMount = useRef(true);

  const userStore: UserState = useSelector((state: any) => state.userStore);
  const assignedGrandfatherAccountName = userStore.assignedGrandfatherAccountName;
  const assignedAccountNumber = userStore.assignedAccountNumber;
  const grandparentAccount = useSelector((state: any) => state.accountsStore.grandparentAccount);
  const grandparentAccountError = useSelector((state: any) => state.accountsStore.grandparentAccountError);
  const grandparentAccountLoading = useSelector((state: any) => state.accountsStore.grandparentAccountLoading);
  const grandparentAccountLoaded = useSelector((state: any) => state.accountsStore.grandparentAccountLoaded);
  const cashOrdersListLoading = useSelector((state: any) => state.cashStore.cashOrdersListLoading);

  const loggedInUser = userStore.loggedInUser;
  const hasMultipleAccounts = loggedInUser.accounts?.length > 1;
  const isAdmin = userStore.isAmpolAdmin;
  const portalType = userStore.portalType;

  const switchAccount = (clientNumber) => {
    if (clientNumber !== assignedAccountNumber) {
      const account = loggedInUser.accounts?.find((x) => x.accountNumber === clientNumber);
      window.dataLayer.push({
        event: 'Card_Portal_Account_Dropdown_ Selector',
        account_number: account.accountNumber,
      });

      trackCustomEvent('Top Level Account Selector', loggedInUser, clientNumber, 'User selected a Top Level Account.');

      dispatch(
        setAssignedGrandparentAccount({
          ...account,
          navigate,
        })
      );
    }
  };

  useEffect(() => {
    if (isInitialMount.current && portalType) {
      // For account setup
      if (queryString.parse(location.search)?.id_token_hint) {
        const newUrl = location.pathname;
        navigate(newUrl, { replace: true });
      }
      // If first account in dropdown is a cash account
      if (portalType === 'cash' && location.pathname === '/') {
        navigate('/cash', { replace: true });
      }
      // If first account in dropdown is a card account
      else if (portalType === 'card' && location.pathname === '/cash') {
        navigate('/', { replace: true });
      }
      isInitialMount.current = false;
    }
  }, [portalType]);

  const searchAccount = (values: any) => {
    const { clientNumber } = values;

    if (clientNumber.match(validAccountRegex)) {
      dispatch(getCashOrdersListReset());

      dispatch(getGrandparentAccountNameRequest(clientNumber));

      trackCustomEvent(
        'Search Account Number',
        loggedInUser,
        undefined,
        'Entered an account number to search for an Ampol Cash Account',
        { selectedAccount: clientNumber }
      );
    }
  };

  const hideSearch = () => {
    if (grandparentAccount && showSearch) {
      setShowSearch(false);
    }
  };

  useEffect(() => {
    if (grandparentAccountLoaded) {
      hideSearch();
    }
  }, [grandparentAccountLoaded]);

  // To show Account Not Found error message when account search fails
  useEffect(() => {
    if (isMounted.current) {
      setSearchValueChanged(true);
    } else {
      isMounted.current = true;
    }

    const timer = setTimeout(() => {
      setSearchValueChanged(false);
    }, 500);
    return () => {
      if (timer) clearInterval(timer);
      if (grandparentAccountError) dispatch(getGrandparentAccountNameReset());
    };
  }, [grandparentAccountError]);

  const searchSchema = yup.object().shape({
    clientNumber: string()
      .nullable()
      .matches(validAccountRegex, { excludeEmptyString: true, message: Validation.ACCOUNT_NUMBER_TEN_DIGITS })
      .test('clientNumber', Validation.ACCOUNT_NUMBER_NOT_FOUND, () => !grandparentAccountError),
  });

  const getSelectableAccounts = () => {
    return loggedInUser.accounts
      .filter((acc) => Env.isAmpolCashEnabled() || getPortalType(acc.grandParentAccountNumber) !== 'cash')
      .map((acc) => ({
        label: `${acc.name} - ${acc.accountNumber}`,
        value: acc.accountNumber,
      }));
  };

  return (
    <>
      <div className={'header-account-selector'}>
        {!hasMultipleAccounts && !isAdmin && <h3>{assignedGrandfatherAccountName}</h3>}
        {/* Only for Dev Admin Account in Customer Portal */}
        {isAdmin && (
          <OutsideClickHandler onOutsideClick={hideSearch}>
            {grandparentAccount && !showSearch && (
              <h3>
                <button type='button' className='header-account-selector__toggle' onClick={() => setShowSearch(true)}>
                  <span>{assignedGrandfatherAccountName}</span> <Icon url={'icons/search.svg'} />
                </button>
              </h3>
            )}
            {showSearch && (
              <Form
                schema={searchSchema}
                onChange={searchAccount}
                validationMode={FormValidationMode.onChange}
                id={'searchAccount'}
                triggerValidation={searchValueChanged}
              >
                <Form.Input
                  hasFocus={true}
                  className='header-account-selector__field header-account-selector__field--input'
                  label={'Enter 10 digit account number'}
                  placeholder={'Search account'}
                  name={'clientNumber'}
                  inputSuffix={<Icon url='/icons/search.svg' />}
                />
              </Form>
            )}
          </OutsideClickHandler>
        )}

        {hasMultipleAccounts && !isAdmin && (
          <DropdownSearch
            data={getSelectableAccounts()}
            value={assignedAccountNumber}
            onChange={(value) => switchAccount(value)}
            textOverflow='ellipsis'
            allowClear={false}
            type={'dropdown'}
            searchOptions
          />
        )}
      </div>
      {allowCashAccountsOnly && (grandparentAccountLoading || cashOrdersListLoading) && isAdmin && <PageLoader />}
    </>
  );
};

export default HeaderAccountSelector;
